import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import data from "../content/case-studies.json";
import { draftPath } from "./DraftLayout";

const isPlaceholder = (v) => String(v || "").startsWith("[PLACEHOLDER");

export function WinsTeaser({ cases, exclude = [], title = "Client results, in their numbers." }) {
  const wins = (cases || data.cases.filter((c) => c.status === "published" && !exclude.includes(c.slug)))
    .map((c) => ({ ...c, stat: (c.heroStats || []).find((s) => !isPlaceholder(s.value)), move: (c.moves || [])[0] }))
    .filter((c) => c.stat);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (wins.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % wins.length), 6000);
    return () => clearInterval(t);
  }, [wins.length]);
  if (!wins.length) return null;
  const w = wins[i % wins.length];

  return (
    <div data-testid="wins-teaser" className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-[#0B0275] via-navy to-[#050048] p-8 text-white shadow-2xl md:p-11">
      {w.teamPhoto && (
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-2/5 md:block">
          <img src={w.teamPhoto.src} alt={w.teamPhoto.alt || w.client} className="h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/40 to-transparent" />
        </div>
      )}
      <div className="relative flex items-center justify-between gap-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">{title}</div>
        {wins.length > 1 && (
          <div className="flex gap-1.5">
            {wins.map((x, j) => (
              <button key={x.slug} aria-label={x.client} data-testid={`wins-dot-${x.slug}`} onClick={() => setI(j)} className={`h-2 rounded-full transition-all ${j === i ? "w-6 bg-pink" : "w-2 bg-white/30 hover:bg-white/60"}`} />
            ))}
          </div>
        )}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={w.slug} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="relative mt-6 flex flex-1 flex-col justify-center md:max-w-[60%]">
          <div className="flex items-center gap-3">
            {w.logoLight && <img src={w.logoLight} alt={w.client} className="h-6 w-auto" />}
            <span className="text-sm text-white/60">{w.client}</span>
          </div>
          <div className="mt-4 text-6xl font-black leading-none tracking-[-0.04em] md:text-7xl">{w.stat.value}</div>
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">{w.stat.label}</div>
          {w.move && <p className="mt-6 max-w-lg text-base leading-relaxed text-white/85 md:text-lg"><span className="font-bold text-white">What we changed: </span>{w.move.title} {w.move.body}</p>}
          <Link to={`${draftPath("/case-studies")}/${w.slug}`} data-testid={`wins-read-${w.slug}`} className="mt-8 inline-flex self-start items-center gap-2 rounded-full bg-pink px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-pink-dark">
            Read the case study <ArrowRight size={16} />
          </Link>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
